import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, FileSearch, AlertTriangle } from "lucide-react";

export default function Plagiarism() {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (text.trim().length < 50) {
      toast.error("Provide at least 50 characters");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/plagiarism/check", { text });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="plagiarism-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ETHICS · PLAGIARISM CHECK</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Pre-submission <em className="italic text-kb-primary">similarity check</em>.
      </h1>
      <div className="mt-2 inline-flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        <span>MOCKED ENGINE in v1 · Production deployment connects Turnitin / iThenticate.</span>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <form onSubmit={run} className="lg:col-span-7 kb-card p-7" data-testid="plagiarism-form">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PASTE YOUR DRAFT</div>
          <textarea
            rows={14}
            required
            name="text"
            className="kb-input mt-4 resize-none font-mono text-sm leading-relaxed"
            placeholder="Paste your abstract, section, or full draft (min 50 chars)…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="plagiarism-input"
          />
          <div className="mt-4 flex items-center justify-between">
            <div className="kb-mono text-[10px] text-kb-ink-light">{text.length} characters · {text.trim().split(/\s+/).filter(Boolean).length} words</div>
            <button type="submit" disabled={loading} className="kb-btn-primary" data-testid="plagiarism-run-btn">
              <FileSearch className="w-4 h-4" /> {loading ? "Analysing…" : "Run check"}
            </button>
          </div>
        </form>

        <div className="lg:col-span-5">
          {!result ? (
            <div className="kb-card p-7 text-center" data-testid="plagiarism-empty">
              <ShieldCheck className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
              <div className="font-serif text-lg text-kb-ink mt-3">No check run yet</div>
              <p className="text-sm text-kb-ink-muted mt-2">Paste your text and run the check to see similarity and suspected matches.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="plagiarism-result">
              <div className="kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SIMILARITY SCORE</div>
                <div className="font-mono text-5xl text-kb-primary mt-2">{result.similarity_pct}%</div>
                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${result.similarity_pct < 20 ? "bg-emerald-500" : result.similarity_pct < 35 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${Math.min(100, result.similarity_pct)}%` }} />
                </div>
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-3">
                  {result.similarity_pct < 20 ? "ACCEPTABLE FOR MOST VENUES" : result.similarity_pct < 35 ? "NEEDS PARAPHRASING" : "HIGH — REVISE BEFORE SUBMISSION"}
                </div>
              </div>
              <div className="kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUSPECTED MATCHES</div>
                {result.matches.length === 0 ? (
                  <div className="text-sm text-kb-ink-muted mt-3">No notable matches.</div>
                ) : (
                  <ul className="mt-3 space-y-3">
                    {result.matches.map((m, i) => (
                      <li key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`plagiarism-match-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">MATCH {i+1}</div>
                          <span className="font-mono text-xs text-kb-primary">{m.similarity_pct}%</span>
                        </div>
                        <div className="text-xs text-kb-ink mt-2 leading-relaxed line-clamp-3">{m.snippet}</div>
                        <a href={m.source_url} target="_blank" rel="noreferrer" className="kb-mono text-[10px] text-kb-ink-light hover:text-kb-primary mt-2 inline-block">{m.source_title}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
